import { priceDecimalsFor } from 'hopcharts';

// toLocaleString with options builds a new Intl.NumberFormat on every call, which is what made price
// formatting show up in page-load profiles; one formatter per decimal count is built once instead.
const grouped = new Map<number, Intl.NumberFormat>();

/** Price with sensible decimals for its size and thousands separators. */
export function formatPrice(value: number | undefined | null, decimals?: number): string {
  if (value === undefined || value === null || !Number.isFinite(value)) return '—';
  const d = decimals ?? priceDecimalsFor(value);
  let format = grouped.get(d);
  if (!format) {
    format = new Intl.NumberFormat('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
    grouped.set(d, format);
  }
  return format.format(value);
}

/** Signed percent, e.g. "+2.31%" / "−0.85%" (true minus sign). */
export function formatPercent(value: number | undefined | null, digits = 2): string {
  if (value === undefined || value === null || !Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${Math.abs(value).toFixed(digits)}%`;
}

/** Compact amount: 1.2B, 340.5M, 12.3K. */
export function formatCompact(value: number | undefined | null, digits = 2): string {
  if (value === undefined || value === null || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs >= 1e12) return `${(value / 1e12).toFixed(digits)}T`;
  if (abs >= 1e9) return `${(value / 1e9).toFixed(digits)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(digits)}M`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(digits)}K`;
  return value.toFixed(abs >= 1 ? 2 : 4);
}

/** Money in a quote currency, compact: "$1.23B" for USD-like quotes, "1.23B EUR" otherwise. */
export function formatQuoteVolume(value: number | undefined, quote: string): string {
  if (value === undefined || !Number.isFinite(value)) return '—';
  if (['USD', 'USDT', 'USDC', 'FDUSD', 'DAI', 'TUSD'].includes(quote)) return `$${formatCompact(value)}`;
  if (quote === 'EUR') return `€${formatCompact(value)}`;
  return `${formatCompact(value)} ${quote}`;
}

export function changeClass(value: number | undefined | null): string {
  if (value === undefined || value === null || !Number.isFinite(value) || value === 0) return 'text-fg-2';
  return value > 0 ? 'text-up' : 'text-down';
}
